import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ondit/ui";
import { TrafficSetup } from "~/common/settings/tracking";
import { useTabParam } from "~/core/hooks";
import { useT } from "~/core/i18n";
import { Hint } from "~/core/ui/page";
import { BadgeSetup } from "~/modules/analytics/pages/badge";
import { CrawlerSetup } from "~/modules/analytics/pages/crawlers";
import { GoalsSetup } from "~/modules/analytics/pages/tracking/goals";
import { InstallCheck } from "~/modules/analytics/pages/tracking/live";
import { PageSpeed } from "~/modules/analytics/pages/tracking/vitals";

const TABS = ["tracking", "check", "goals", "speed", "badge", "crawlers"] as const;

const HINTS: Record<(typeof TABS)[number], string> = {
	tracking: "traffic.setupHint",
	check: "live.lede",
	goals: "goals.lede",
	speed: "vitals.lede",
	badge: "badge.lede",
	crawlers: "crawlers.ownSiteHint",
};

/**
 * Everything placed on the brand's own site, in one section.
 *
 * The tracking snippet, the goals it counts, the checks run against it and the
 * two embeds were five rows spread over two stages of the loop, where they read
 * as measurements. They are set up once and revisited when a number looks
 * wrong, which is the shelf the rest of Settings already is.
 */
export function SiteSetup({ entityId }: { entityId: string }) {
	const t = useT();
	const [tab, setTab] = useTabParam(TABS);

	return (
		<Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-(--s-6)">
			<div className="row">
				<TabsList aria-label={t("settings.section.site")}>
					{TABS.map((id) => (
						<TabsTrigger key={id} value={id}>
							{t(`site.tab.${id}`)}
						</TabsTrigger>
					))}
				</TabsList>
				<Hint text={t(HINTS[tab])} />
			</div>
			<TabsContent value={tab}>
				{tab === "tracking" ? (
					<TrafficSetup entityId={entityId} />
				) : tab === "check" ? (
					<InstallCheck entityId={entityId} />
				) : tab === "goals" ? (
					<GoalsSetup entityId={entityId} />
				) : tab === "speed" ? (
					<PageSpeed entityId={entityId} />
				) : tab === "badge" ? (
					<BadgeSetup entityId={entityId} />
				) : (
					<CrawlerSetup entityId={entityId} />
				)}
			</TabsContent>
		</Tabs>
	);
}
